import React, { useState, useMemo, useEffect } from 'react';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import API_BASE_URL from '../config/apiConfig.js';
import fetchApi from '../utils/api.js';
import { usePermissionContext } from '../contexts/PermissionContext.js';
import SearchBar from '../components/ui/SearchBar';
import PageSkeleton from '../components/ui/PageSkeleton';
import DataTable from '../components/ui/DataTable';
function BankList({ onAdd, onEdit }) {
    const navigate = useNavigate();
    const { hasPermission } = usePermissionContext();
    const [rows, setRows] = useState([]);
    const [totalEntries, setTotalEntries] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [selectedIdx, setSelectedIdx] = useState(0);

    // Keyboard navigation: Up, Down, Enter
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (rows.length === 0) return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIdx((prev) => (prev + 1) % rows.length);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIdx((prev) => (prev - 1 + rows.length) % rows.length);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                const selectedItem = rows[selectedIdx];
                if (selectedItem) {
                    navigate(`/admin/settings/bank/ledger/${selectedItem._id}`);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [rows, selectedIdx, navigate]);

    const fetchBanks = async (search = '', page = 1, limit = 10) => {
        try {
            if (!search) setLoading(true);
            const result = await fetchApi(`/bank?search=${search}&page=${page}&limit=${limit}`);

            if (result && result.banks) {
                setRows(result.banks);
                setTotalEntries(result.total || 0);
                setTotalPages(result.pages || 1);
            }
        } catch (error) {
            console.error('Error fetching banks:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchBanks(searchQuery, currentPage, itemsPerPage);
        }, 200);

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery, currentPage, itemsPerPage]);

    const handleEdit = (bank) => {
        if (onEdit) onEdit(bank);
    };

    const handleAdd = () => {
        if (onAdd) onAdd();
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: 'Do you want to delete this bank?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = sessionStorage.getItem('token');
                    const response = await fetch(`${API_BASE_URL}/bank/${id}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` }
                    });

                    if (response.ok) {
                        setRows(prevData => prevData.filter(item => item._id !== id));
                        Swal.fire('Deleted!', 'Bank has been deleted.', 'success');
                    } else {
                        const errData = await response.json();
                        toast.error(errData.message || 'Failed to delete bank');
                    }
                } catch (error) {
                    console.error("Delete Error:", error);
                    toast.error("Server error");
                }
            }
        });
    };

    const handlePrint = async (bankId) => {
        try {
            const result = await fetchApi(`/bank/ledger/${bankId}`);
            if (result && result.ledger) {
                const { bank, ledger } = result;
                const companyInfo = JSON.parse(sessionStorage.getItem('company')) || {};
                
                const printWindow = window.open('', '_blank');
                const html = `
                    <html>
                        <head>
                            <title>Bank Statement - ${bank.bankName}</title>
                            <style>
                                body { font-family: Arial, sans-serif; padding: 20px; }
                                .header { text-align: center; margin-bottom: 20px; border: 1px solid #000; padding: 0; overflow: hidden; }
                                .header-top { padding: 10px; border-bottom: 1px solid #000; }
                                .company-name { font-size: 24px; font-weight: bold; margin-bottom: 5px; }
                                .gst-no { font-size: 14px; margin-bottom: 5px; }
                                .address { font-size: 14px; margin-bottom: 5px; }
                                .bank-name { font-size: 16px; font-weight: bold; padding: 8px; background-color: #fff; }
                                table { width: 100%; border-collapse: collapse; margin-top: 10px; border: 1px solid #000; }
                                th, td { border: 1px solid #000; padding: 8px; text-align: left; font-size: 13px; }
                                th { background-color: #fff; font-weight: bold; }
                                .text-right { text-align: right; }
                                .text-center { text-align: center; }
                                .footer { margin-top: 20px; text-align: right; }
                                @media print {
                                    .no-print { display: none; }
                                    body { padding: 0; }
                                }
                            </style>
                        </head>
                        <body>
                            <div class="header">
                                <div class="header-top">
                                    <div class="company-name">${companyInfo.companyName || 'ASENSIA INDUSTRY LLP'}</div>
                                    <div class="gst-no">GST NO: ${companyInfo.gstNumber || '24ACLFA6445J1Z7'}</div>
                                    <div class="address">${companyInfo.address || 'PLOT NO. 17, LALPAR INDUSTRIAL ESTATE-1, LALPAR, 8 A NATIONAL HIGHWAY, MORBI. GUJARAT 363642'}</div>
                                </div>
                                <div class="bank-name">${bank.bankName}</div>
                            </div>
                            <table>
                                <thead>
                                    <tr>
                                        <th class="text-center" style="width: 50px;">SR NO</th>
                                        <th class="text-center" style="width: 100px;">DATE</th>
                                        <th>NARRATION</th>
                                        <th class="text-right" style="width: 120px;">CREDIT</th>
                                        <th class="text-right" style="width: 120px;">DEBIT</th>
                                        <th class="text-right" style="width: 120px;">BALANCE</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    ${ledger.map((entry, index) => `
                                        <tr>
                                            <td class="text-center">${index + 1}</td>
                                            <td class="text-center">${new Date(entry.date).toLocaleDateString('en-GB')}</td>
                                            <td>${entry.narration}</td>
                                            <td class="text-right">${entry.credit > 0 ? entry.credit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}</td>
                                            <td class="text-right">${entry.debit > 0 ? entry.debit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}</td>
                                            <td class="text-right">${entry.balance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                        </tr>
                                    `).join('')}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <th colspan="3" class="text-right">TOTAL</th>
                                        <th class="text-right">${ledger.reduce((sum, e) => sum + e.credit, 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</th>
                                        <th class="text-right">${ledger.reduce((sum, e) => sum + e.debit, 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</th>
                                        <th class="text-right">${ledger[ledger.length - 1]?.balance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</th>
                                    </tr>
                                </tfoot>
                            </table>
                            <script>
                                window.onload = function() { 
                                    setTimeout(() => {
                                        window.print(); 
                                        window.close();
                                    }, 500);
                                }
                            </script>
                        </body>
                    </html>
                `;
                printWindow.document.write(html);
                printWindow.document.close();
            }
        } catch (error) {
            console.error("Print Error:", error);
            toast.error("Failed to generate print view");
        }
    };

    const showingFrom = totalEntries === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const showingTo = Math.min(totalEntries, (currentPage - 1) * itemsPerPage + rows.length);

    const pages = useMemo(() => {
        const out = [];
        const maxButtons = 5;
        let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
        let end = Math.min(totalPages, start + maxButtons - 1);
        start = Math.max(1, end - maxButtons + 1);
        for (let i = start; i <= end; i += 1) out.push(i);
        return out;
    }, [currentPage, totalPages]);

    if (loading) {
        return <PageSkeleton />;
    }

    const columns = [
        {
            key: 'bankName',
            label: 'NAME',
            render: (item, idx) => (
                <div
                    className={`party-name-box ${idx === selectedIdx ? 'party-name-box--active' : ''}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedIdx(idx);
                        navigate(`/admin/settings/bank/ledger/${item._id}`);
                    }}
                >
                    {item.bankName}
                </div>
            )
        },
        { key: 'branchName', label: 'BRANCH NAME' },
        { key: 'ifscCode', label: 'IFSC CODE' },
        { key: 'accountNumber', label: 'ACCOUNT NUMBER' },
        { key: 'openingBalance', label: 'OPENING BALANCE', render: (item) => (Number(item.openingBalance) || 0).toFixed(2) },
        { key: 'bankAddress', label: 'ADDRESS' },
        { key: 'credit', label: 'TOTAL CREDIT', render: (item) => item.credit || '0.00' },
        { key: 'debit', label: 'TOTAL DEBIT', render: (item) => item.debit || '0.00' },
        { key: 'balance', label: 'BALANCE', render: (item) => item.balance || '0.00' },
        {
            key: 'print',
            label: 'Print',
            render: (item) => (
                <button 
                    className="list-page-btn" 
                    style={{ backgroundColor: '#0b3a54', fontSize: '10px', padding: '4px 8px', color:'white', borderRadius: '4px' }}
                    onClick={() => handlePrint(item._id)}
                >
                    Print
                </button>
            )
        },
        {
            key: 'actions',
            label: 'ACTION',
            render: (item) => (
                <div className="list-page-action-icons">
                    {hasPermission('Bank', 'update') && (
                        <button className="list-page-icon-btn" onClick={() => handleEdit(item)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                        </button>
                    )}
                    {hasPermission('Bank', 'delete') && (
                        <button className="list-page-icon-btn" onClick={() => handleDelete(item._id)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                        </button>
                    )}
                </div>
            )
        }
    ];

    const footerRow = rows.length > 0 ? (
        <tr style={{ backgroundColor: '#eaeaea', fontWeight: 'bold' }}>
            <td colSpan={6} style={{ textAlign: 'right', fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>TOTAL</td>
            <td style={{ fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>
                {rows.reduce((sum, item) => sum + (parseFloat(item.credit) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </td>
            <td style={{ fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>
                {rows.reduce((sum, item) => sum + (parseFloat(item.debit) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </td>
            <td style={{ fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>
                {rows.reduce((sum, item) => sum + (parseFloat(item.balance) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </td>
            <td></td>
            <td></td>
        </tr>
    ) : null;

    return (
        <div className="list-page-container">
            <div className="list-page-header">
                <h1 className="list-page-title">Bank List</h1>
                <div className="list-page-action-buttons">
                    {hasPermission('Bank', 'add') && (
                        <button className="list-page-btn list-page-btn-add" onClick={handleAdd}>
                            Add Bank
                        </button>
                    )}
                </div>
            </div>

            <DataTable
                columns={columns}
                data={rows}
                loading={loading}
                emptyMessage="No records found"
                page={currentPage}
                pageSize={itemsPerPage}
                totalEntries={totalEntries}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                onPageSizeChange={setItemsPerPage}
                footerRow={footerRow}
                onRowClick={(item, idx) => {
                    setSelectedIdx(idx);
                }}
                rowClassName={(item, idx) => idx === selectedIdx ? 'selected-row' : ''}
                searchBar={
                    <div style={{ display: 'flex', justifyContent: 'flex-end', background: 'transparent', padding: '0', boxShadow: 'none', marginBottom: '5px' }}>
                        <SearchBar
                            label="SEARCH"
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setCurrentPage(1);
                            }}
                            placeholder="Search banks..."
                            style={{ width: '300px' }}
                        />
                    </div>
                }
            />
        </div>
    );
}

export default BankList;
